import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field, useField } from 'formik';
import * as Yup from 'yup';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronDown, Loader2, ShieldCheck, UserRound, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { getCountries, getCountry, getStates } from 'country-state-picker';
import { getLoading, getStaff } from '../redux/selectors';
import { create_staff } from '../redux/reducer';

const countries = getCountries();

const ROLES = [
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full access to all settings',
    icon: ShieldCheck
  },
  {
    value: 'staff',
    label: 'Staff',
    description: 'Day-to-day content access',
    icon: UserRound
  }
];

const validationSchema = Yup.object().shape({
  first_name: Yup.string().trim().required('First name is required'),
  last_name: Yup.string().trim().required('Last name is required'),
  email: Yup.string().email('Enter a valid email').required('Email is required'),
  phone: Yup.string().trim().required('Phone is required'),
  role: Yup.string().required('Select a role'),
  country: Yup.string().required('Country is required'),
  state: Yup.string().required('State is required'),
  city: Yup.string().trim().required('City is required')
});

const initialValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  role: '',
  country: '',
  state: '',
  city: ''
};

const inputClass = (hasError) =>
  `w-full h-[42px] px-3 text-[14px] text-[#1F2937] bg-white rounded-[10px] border transition focus:outline-none focus:ring focus:ring-opacity-30 disabled:bg-[#F9FAFB] disabled:text-[#9CA3AF] ${
    hasError
      ? 'border-[#EF4444] focus:ring-[#FCA5A5]'
      : 'border-[#E0E0E0] focus:border-[#549A9A] focus:ring-[#ACD1D1]'
  }`;

function FieldShell({ label, name, className = '', children }) {
  const [, meta] = useField(name);
  const error = meta.touched && meta.error;
  return (
    <div className={className}>
      <label
        htmlFor={name}
        className="block mb-1.5 text-[13px] font-medium text-[#374151]"
      >
        {label}
      </label>
      {children(Boolean(error))}
      {error && <p className="mt-1 text-[12px] text-[#EF4444]">{error}</p>}
    </div>
  );
}

function TextField({ label, name, className, ...rest }) {
  return (
    <FieldShell label={label} name={name} className={className}>
      {(hasError) => (
        <Field id={name} name={name} className={inputClass(hasError)} {...rest} />
      )}
    </FieldShell>
  );
}

function SelectField({ label, name, placeholder, className, children, ...rest }) {
  return (
    <FieldShell label={label} name={name} className={className}>
      {(hasError) => (
        <div className="relative">
          <Field
            as="select"
            id={name}
            name={name}
            className={`${inputClass(hasError)} appearance-none pr-9`}
            {...rest}
          >
            <option value="">{placeholder}</option>
            {children}
          </Field>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 text-[#9CA3AF] -translate-y-1/2 pointer-events-none"
          />
        </div>
      )}
    </FieldShell>
  );
}

function AddStaff({ isOpen, onOpenChange }) {
  const dispatch = useDispatch();
  const loading = useSelector(getLoading);
  const staffCount = useSelector(getStaff).length;
  const [states, setStates] = useState([]);
  const submittedCount = useRef(null);

  // Close once the new member lands in the list; keep the form open on errors.
  useEffect(() => {
    if (submittedCount.current === null || loading) return;
    if (staffCount > submittedCount.current) onOpenChange(false);
    submittedCount.current = null;
  }, [loading, staffCount, onOpenChange]);

  useEffect(() => {
    if (!isOpen) setStates([]);
  }, [isOpen]);

  const handleSubmit = (values) => {
    submittedCount.current = staffCount;
    dispatch(create_staff({ ...values, country: getCountry(values.country)?.name }));
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex flex-col w-[calc(100%-2rem)] max-w-[600px] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl font-outfit focus:outline-none">
          <div className="flex gap-4 justify-between items-start px-6 pt-6 pb-4 border-b border-[#E9E9E9]">
            <div>
              <Dialog.Title className="text-[18px] font-semibold text-[#1F2937]">
                Add staff member
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-[14px] text-[#6B7280]">
                They'll be able to sign in to the admin dashboard with this email.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form className="flex flex-col min-h-0">
                <div className="overflow-y-auto px-6 py-5 space-y-6">
                  <section>
                    <h3 className="mb-3 text-[12px] font-semibold tracking-wide uppercase text-[#9CA3AF]">
                      Personal details
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <TextField
                        label="First name"
                        name="first_name"
                        placeholder="Jane"
                      />
                      <TextField
                        label="Last name"
                        name="last_name"
                        placeholder="Doe"
                      />
                      <TextField
                        label="Email"
                        name="email"
                        type="email"
                        placeholder="jane@akaani.com"
                      />
                      <TextField
                        label="Phone"
                        name="phone"
                        type="tel"
                        placeholder="+234 800 000 0000"
                      />
                    </div>
                  </section>

                  <section>
                    <h3 className="mb-3 text-[12px] font-semibold tracking-wide uppercase text-[#9CA3AF]">
                      Role
                    </h3>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {ROLES.map(({ value, label, description, icon: Icon }) => {
                        const selected = values.role === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setFieldValue('role', value)}
                            className={`flex gap-3 items-start p-3 text-left rounded-xl border transition-colors ${
                              selected
                                ? 'border-[#549A9A] bg-[#549A9A]/5 ring-1 ring-[#549A9A]'
                                : 'border-[#E0E0E0] hover:border-[#BFD9D9]'
                            }`}
                          >
                            <span
                              className={`grid place-items-center w-9 h-9 shrink-0 rounded-lg ${
                                selected
                                  ? 'bg-[#549A9A] text-white'
                                  : 'bg-[#F3F4F6] text-[#6B7280]'
                              }`}
                            >
                              <Icon size={18} />
                            </span>
                            <span>
                              <span className="block text-[14px] font-medium text-[#1F2937]">
                                {label}
                              </span>
                              <span className="block text-[12px] text-[#6B7280]">
                                {description}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {touched.role && errors.role && (
                      <p className="mt-1 text-[12px] text-[#EF4444]">
                        {errors.role}
                      </p>
                    )}
                  </section>

                  <section>
                    <h3 className="mb-3 text-[12px] font-semibold tracking-wide uppercase text-[#9CA3AF]">
                      Location
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <SelectField
                        label="Country"
                        name="country"
                        placeholder="Select country"
                        className="sm:col-span-2"
                        onChange={(e) => {
                          setFieldValue('country', e.target.value);
                          setFieldValue('state', '');
                          setStates(e.target.value ? getStates(e.target.value) : []);
                        }}
                      >
                        {countries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </SelectField>
                      <SelectField
                        label="State"
                        name="state"
                        placeholder={
                          values.country ? 'Select state' : 'Select a country first'
                        }
                        disabled={!values.country}
                      >
                        {states.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </SelectField>
                      <TextField label="City" name="city" placeholder="Lagos" />
                    </div>
                  </section>
                </div>

                <div className="flex gap-3 justify-end px-6 py-4 border-t border-[#E9E9E9] bg-[#F9F9FB] rounded-b-2xl">
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="h-[42px] px-5 rounded-[10px] border border-[#DDDDDD] bg-white text-[14px] font-semibold text-[#333333] hover:bg-[#F3F4F6]"
                    >
                      Cancel
                    </button>
                  </Dialog.Close>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex gap-2 items-center h-[42px] px-5 rounded-[10px] bg-[#549A9A] hover:bg-[#447f7f] text-[14px] font-semibold text-[#E2FFEE] transition-colors disabled:opacity-60"
                  >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? 'Adding…' : 'Add staff'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default AddStaff;
